import { AbnApplicationReason } from "services/company/companyTypes";
import { AbnSoleTraderQuestion, YesNoAnswer } from "./abnSoleTraderConstants";

interface Answer {
  id: string;
  answer: string;
  nextQuestionId: string;
  terminateFlow?: boolean;
}

interface Question {
  id: string;
  question: string;
  answersType: "radio" | "select" | "input";
  answers: Record<string, Answer>;
}

interface AbnSoleTraderFlowConfig {
  firstQuestionId: string;
  questions: Record<string, Question>;
}

export const ABN_SOLE_TRADER_FLOW_CONFIG: AbnSoleTraderFlowConfig = {
  firstQuestionId: AbnSoleTraderQuestion.WhyApplyingForAbn,
  questions: {
    [AbnSoleTraderQuestion.WhyApplyingForAbn]: {
      id: AbnSoleTraderQuestion.WhyApplyingForAbn,
      question: "Why are you applying for ABN?",
      answersType: "radio",
      answers: {
        [AbnApplicationReason.NewBusinessInAustralia]: {
          id: AbnApplicationReason.NewBusinessInAustralia,
          answer: "New business in Australia",
          nextQuestionId: AbnSoleTraderQuestion.FirstTimeRunningBusiness,
        },
        [AbnApplicationReason.ToReceivePaymentForServices]: {
          id: AbnApplicationReason.ToReceivePaymentForServices,
          answer: "To receive payment for services",
          nextQuestionId: AbnSoleTraderQuestion.FirstTimeRunningBusiness,
        },
        [AbnApplicationReason.PurchasedExistingBusiness]: {
          id: AbnApplicationReason.PurchasedExistingBusiness,
          answer: "Purchased existing business",
          nextQuestionId: AbnSoleTraderQuestion.CurrentlyHaveOrPreviouslyHadAbn,
        },
        [AbnApplicationReason.ContractorSubcontractor]: {
          id: AbnApplicationReason.ContractorSubcontractor,
          answer: "Contractor/Subcontractor",
          nextQuestionId: AbnSoleTraderQuestion.FirstTimeSelfEmployed,
        },
        [AbnApplicationReason.InvestmentPurposes]: {
          id: AbnApplicationReason.InvestmentPurposes,
          answer: "Investment purposes",
          nextQuestionId: AbnSoleTraderQuestion.FirstTimeRunningBusiness,
        },
        [AbnApplicationReason.ChangeInBusinessStructure]: {
          id: AbnApplicationReason.ChangeInBusinessStructure,
          answer: "Change in business structure",
          nextQuestionId: AbnSoleTraderQuestion.PreviousAbn,
        },
      },
    },
    [AbnSoleTraderQuestion.CurrentlyHaveOrPreviouslyHadAbn]: {
      id: AbnSoleTraderQuestion.CurrentlyHaveOrPreviouslyHadAbn,
      question: "Do you currently have or previously had an ABN?",
      answersType: "radio",
      answers: {
        [YesNoAnswer.Yes]: {
          id: YesNoAnswer.Yes,
          answer: "Yes",
          nextQuestionId: AbnSoleTraderQuestion.BusinessDescription,
        },
        [YesNoAnswer.No]: {
          id: YesNoAnswer.No,
          answer: "No",
          nextQuestionId: AbnSoleTraderQuestion.BusinessDescription,
        },
      },
    },
    [AbnSoleTraderQuestion.FirstTimeRunningBusiness]: {
      id: AbnSoleTraderQuestion.FirstTimeRunningBusiness,
      question: "Is this your first time running a business or being self-employed in Australia?",
      answersType: "radio",
      answers: {
        [YesNoAnswer.Yes]: {
          id: YesNoAnswer.Yes,
          answer: "Yes",
          nextQuestionId: AbnSoleTraderQuestion.CurrentlyHaveOrPreviouslyHadAbn,
        },
        [YesNoAnswer.No]: {
          id: YesNoAnswer.No,
          answer: "No",
          nextQuestionId: AbnSoleTraderQuestion.CurrentlyHaveOrPreviouslyHadAbn,
        },
      },
    },
    [AbnSoleTraderQuestion.PreviousAbn]: {
      id: AbnSoleTraderQuestion.PreviousAbn,
      question: "What was the ABN previously used?",
      answersType: "input",
      answers: {
        continue: {
          id: "continue",
          answer: "Continue",
          nextQuestionId: AbnSoleTraderQuestion.BusinessDescription,
        },
      },
    },
    [AbnSoleTraderQuestion.FirstTimeSelfEmployed]: {
      id: AbnSoleTraderQuestion.FirstTimeSelfEmployed,
      question: "Is this your first time being self-employed as a contractor/subcontractor in Australia?",
      answersType: "radio",
      answers: {
        [YesNoAnswer.Yes]: {
          id: YesNoAnswer.Yes,
          answer: "Yes",
          nextQuestionId: AbnSoleTraderQuestion.CurrentlyHaveOrPreviouslyHadAbn,
        },
        [YesNoAnswer.No]: {
          id: YesNoAnswer.No,
          answer: "No",
          nextQuestionId: AbnSoleTraderQuestion.CurrentlyHaveOrPreviouslyHadAbn,
        },
      },
    },
    [AbnSoleTraderQuestion.BusinessDescription]: {
      id: AbnSoleTraderQuestion.BusinessDescription,
      question: "What is your business?",
      answersType: "input",
      answers: {
        continue: {
          id: "continue",
          answer: "Continue",
          nextQuestionId: AbnSoleTraderQuestion.BusinessAddress,
        },
      },
    },
    [AbnSoleTraderQuestion.BusinessAddress]: {
      id: AbnSoleTraderQuestion.BusinessAddress,
      question: "Main business activity address",
      answersType: "input",
      answers: {
        continue: {
          id: "continue",
          answer: "Continue",
          nextQuestionId: AbnSoleTraderQuestion.PostalAddress,
        },
      },
    },
    [AbnSoleTraderQuestion.PostalAddress]: {
      id: AbnSoleTraderQuestion.PostalAddress,
      question: "Set your postal address",
      answersType: "input",
      answers: {
        continue: {
          id: "continue",
          answer: "Continue",
          nextQuestionId: AbnSoleTraderQuestion.BusinessActivities,
        },
      },
    },
    [AbnSoleTraderQuestion.BusinessActivities]: {
      id: AbnSoleTraderQuestion.BusinessActivities,
      question: "What is your main business activity?",
      answersType: "input",
      answers: {
        continue: {
          id: "continue",
          answer: "Continue",
          nextQuestionId: AbnSoleTraderQuestion.AgriculturalOperations,
        },
      },
    },
    [AbnSoleTraderQuestion.AgriculturalOperations]: {
      id: AbnSoleTraderQuestion.AgriculturalOperations,
      question: "Do you operate land for agricultural purposes?",
      answersType: "radio",
      answers: {
        [YesNoAnswer.Yes]: {
          id: YesNoAnswer.Yes,
          answer: "Yes, I use land for agriculture",
          nextQuestionId: "0",
        },
        [YesNoAnswer.No]: {
          id: YesNoAnswer.No,
          answer: "No, I don't use land for agriculture",
          nextQuestionId: "0",
        },
      },
    },
  },
};

export const getNextQuestionId = (questionId: string, answerId: string, formData?: Record<string, unknown>): string => {
  const question = ABN_SOLE_TRADER_FLOW_CONFIG.questions[questionId];
  if (!question) {
    return "0";
  }

  // Special handling for BusinessAddress question - check if postal address should be skipped
  if (questionId === AbnSoleTraderQuestion.BusinessAddress) {
    const useBusinessAddressAsPostal = formData?.useBusinessAddressAsPostal;
    if (useBusinessAddressAsPostal === true) {
      // Skip postal address, go directly to business activities
      return AbnSoleTraderQuestion.BusinessActivities;
    }
    // return AbnSoleTraderQuestion.PostalAddress; -- IGNORE UNITIL BACKEND IS DONE --
    return AbnSoleTraderQuestion.BusinessActivities;
  }

  const answer = question.answers[answerId];
  return answer?.nextQuestionId ?? "0";
};

export const isFlowComplete = (nextQuestionId: string): boolean => {
  return nextQuestionId === "0";
};
